import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useTestSessionStore } from '@/store/testSessionStore';
import { formatDuration } from '@/lib/utils';
import { useEffect } from 'react';

export default function TestLayout() {
    const navigate = useNavigate();
    const { sessionId } = useParams();
    const {
        session,
        question,
        timeRemaining,
        isSubmitting,
        isAutosaving,
        lastAutosave
    } = useTestSessionStore();

    // Handle time expiry
    useEffect(() => {
        if (timeRemaining === 0 && session?.status === 'in_progress') {
            // Auto-submit when time expires
            // This is handled by the TestSession page
        }
    }, [timeRemaining, session?.status]);

    const handleExit = () => {
        if (confirm('Are you sure you want to exit? Your progress will be saved.')) {
            navigate('/student/tests');
        }
    };

    const getTimeColorClass = () => {
        if (timeRemaining <= 60) return 'text-error-500 animate-pulse';
        if (timeRemaining <= 300) return 'text-warning-500';
        return 'text-slate-700 dark:text-slate-300';
    };

    return (
        <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex flex-col">
            {/* Test Header */}
            <header className="sticky top-0 z-50 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-sm">
                <div className="h-16 px-4 lg:px-8 flex items-center justify-between">
                    {/* Left: Question Info */}
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleExit}
                            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400"
                            title="Exit Test"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </button>
                        <div>
                            <h1 className="font-semibold text-slate-900 dark:text-white">
                                {question?.title || 'Typing Test'}
                            </h1>
                            <p className="text-xs text-slate-500">
                                {question?.difficulty && (
                                    <span className={`capitalize ${question.difficulty === 'hard' ? 'text-error-500' :
                                            question.difficulty === 'medium' ? 'text-warning-500' :
                                                'text-success-500'
                                        }`}>
                                        {question.difficulty}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>

                    {/* Center: Timer */}
                    <div className="flex items-center gap-2">
                        <svg className={`w-5 h-5 ${getTimeColorClass()}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className={`font-mono text-2xl font-bold ${getTimeColorClass()}`}>
                            {formatDuration(timeRemaining)}
                        </span>
                    </div>

                    {/* Right: Status & Actions */}
                    <div className="flex items-center gap-4">
                        {/* Autosave Status */}
                        <div className="flex items-center gap-2 text-sm">
                            {isAutosaving ? (
                                <>
                                    <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" />
                                    <span className="text-slate-500">Saving...</span>
                                </>
                            ) : lastAutosave ? (
                                <>
                                    <div className="w-2 h-2 bg-success-500 rounded-full" />
                                    <span className="text-slate-500">Saved</span>
                                </>
                            ) : null}
                        </div>

                        {/* Submit Button */}
                        <button
                            className="btn-primary text-sm py-2"
                            disabled={isSubmitting}
                            id="submit-test-btn"
                        >
                            {isSubmitting ? (
                                <>
                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    <span>Submitting...</span>
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>Submit Test</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            {/* Test Content */}
            <main className="flex-1 overflow-hidden">
                <Outlet />
            </main>
        </div>
    );
}
